import { $api } from '../api/queryClient'
import type { components } from '../api/schema'

// Expense statement (spec 2026-10-22-01): the pivot of outgoing finance lines
// by expense category (or account) and by site or building.

export type ExpenseStatement = components['schemas']['ExpenseStatement']
export type ExpenseStatementRow = components['schemas']['ExpenseStatementRow']
export type ExpenseStatementColumn = components['schemas']['ExpenseStatementColumn']
export type ExpenseStatementLine = components['schemas']['ExpenseStatementLine']
export type ExpenseCategoryTaxonomy = components['schemas']['ExpenseCategoryTaxonomy']
export type ExpenseClassification = components['schemas']['ExpenseClassification']
export type ExpenseCategoryLabel = components['schemas']['ExpenseCategoryLabel']

export type StatementView = 'business' | 'accounting'
export type StatementRegime = 'all' | 'current' | 'works'
export type StatementAmount = 'ttc' | 'ht'

export interface StatementQuery {
  from: string
  to: string
  siteUid?: string
  view: StatementView
  regime: StatementRegime
  amount: StatementAmount
}

function toParams(q: StatementQuery) {
  return {
    from: q.from,
    to: q.to,
    site_uid: q.siteUid,
    view: q.view,
    regime: q.regime,
    amount: q.amount,
  }
}

export function useExpenseStatement(org: string, q: StatementQuery) {
  return $api.useQuery('get', '/api/v1/organizations/{orgCode}/expenses/statement', {
    params: { path: { orgCode: org }, query: toParams(q) },
  }, { enabled: !!org })
}

/** The lines behind one cell; a missing row or column reads the whole line. */
export function useExpenseStatementLines(org: string, q: StatementQuery, cell: { row?: string; column?: string } | null) {
  return $api.useQuery('get', '/api/v1/organizations/{orgCode}/expenses/statement/lines', {
    params: { path: { orgCode: org }, query: { ...toParams(q), row: cell?.row, column: cell?.column } },
  }, { enabled: !!org && !!cell })
}

/** The shipped taxonomy: identical for every organization, so cached long. */
export function useExpenseCategories(org: string) {
  return $api.useQuery('get', '/api/v1/organizations/{orgCode}/expenses/categories', {
    params: { path: { orgCode: org } },
  }, { enabled: !!org, staleTime: 60 * 60 * 1000 })
}

export function useExpenseClassification(org: string, expenseUid: string) {
  return $api.useQuery('get', '/api/v1/organizations/{orgCode}/expenses/{expenseUid}/classification', {
    params: { path: { orgCode: org, expenseUid } },
  }, { enabled: !!org && !!expenseUid })
}

/** Picks the label in the reader's language, English as the fallback. */
export function pickLabel(label: ExpenseCategoryLabel | undefined | null, locale: string): string {
  if (!label) return ''
  return locale.startsWith('fr') ? (label.fr || label.en) : (label.en || label.fr)
}
